import type {
  Deployment, DeploymentEvent, ManifestDiffLine, RolloutPhase, ServiceStatus, Service, SyncStatus,
} from "@coremonit/types";
import { makeRng, int, pick } from "./rng";

const AUTHORS = ["alice.wong", "bob.chen", "carol.silva", "diego.perez", "erin.zhao"];
const REPOS = [
  "coremonit/web-storefront", "coremonit/api-gateway", "coremonit/auth-service",
  "coremonit/order-service", "coremonit/payment-service", "coremonit/inventory-service",
  "coremonit/search-service", "coremonit/notification-service", "coremonit/billing-service",
];
const BRANCHES = ["main", "release/2026-q3", "hotfix/pci-cve-2026-321", "feature/rate-limit-v2"];

function shortSha(rng: () => number) {
  return Array.from({ length: 7 }, () => "0123456789abcdef"[Math.floor(rng() * 16)]).join("");
}

function eventsFor(rng: () => number, phase: RolloutPhase): DeploymentEvent[] {
  const t0 = Date.now() - int(rng, 1, 12) * 60 * 60 * 1000;
  const es: DeploymentEvent[] = [
    { ts: new Date(t0).toISOString(),               kind: "created",   message: "Deployment created from image push" },
    { ts: new Date(t0 + 8000).toISOString(),        kind: "synced",    message: "Manifest applied — waiting for rollout" },
    { ts: new Date(t0 + 40000).toISOString(),       kind: "progressed", message: "Replica 1/3 ready" },
    { ts: new Date(t0 + 70000).toISOString(),       kind: "progressed", message: "Replica 2/3 ready" },
  ];
  if (phase === "healthy") es.push({ ts: new Date(t0 + 95000).toISOString(), kind: "progressed", message: "Replica 3/3 ready · healthy" });
  if (phase === "degraded") es.push({ ts: new Date(t0 + 130000).toISOString(), kind: "degraded", message: "ReadinessProbe failing on 1 replica" });
  return es;
}

export function generateDeployments(services: Service[], seed = 42): Deployment[] {
  const rng = makeRng(seed);
  return services.flatMap((s) => {
    // Every service gets 1..4 historical deploys, most recent first.
    const n = int(rng, 1, 4);
    return Array.from({ length: n }, (_, i): Deployment => {
      const phase: RolloutPhase =
        i === 0 && s.status === "critical" ? "degraded" :
        i === 0 && s.status === "warning"  ? "progressing" :
        i === 0                            ? "healthy" :
        "healthy";
      const sync: SyncStatus =
        phase === "healthy"     ? "synced" :
        phase === "progressing" ? "outofsync" :
        phase === "degraded"    ? "outofsync" :
        "synced";
      const health: ServiceStatus =
        phase === "healthy" ? "healthy" :
        phase === "progressing" ? "warning" :
        phase === "degraded" ? "critical" :
        "unknown";
      const rev = int(rng, 100, 999);
      return {
        id: `dep_${s.id}_${rev}`,
        serviceId: s.id,
        serviceName: s.name,
        revision: `v${rev}`,
        previousRevision: `v${rev - 1}`,
        gitCommit: shortSha(rng),
        gitBranch: pick(rng, BRANCHES),
        gitRepo: `github.com/${pick(rng, REPOS)}`,
        imageTag: `${s.name}:${rev}`,
        image: `ghcr.io/coremonit/${s.name}:${rev}`,
        namespace: s.deployment.namespace ?? "default",
        cluster: s.deployment.cluster ?? "prod-eks-01",
        syncStatus: sync,
        healthStatus: health,
        rolloutPhase: phase,
        rolloutProgressPct: phase === "healthy" ? 100 : phase === "progressing" ? int(rng, 40, 88) : int(rng, 20, 65),
        desired: s.replicas.desired,
        available: phase === "healthy" ? s.replicas.desired : s.replicas.available,
        createdAt: new Date(Date.now() - (i * 24 + int(rng, 0, 12)) * 3600000).toISOString(),
        completedAt: phase === "healthy" ? new Date(Date.now() - (i * 24) * 3600000 + int(rng, 60, 300) * 1000).toISOString() : undefined,
        triggeredBy: pick(rng, AUTHORS),
        triggerReason: pick(rng, ["push to main", "manual sync", "auto-promote from staging", "hotfix", "revert"]),
        events: eventsFor(rng, phase),
      };
    });
  });
}

export function manifestDiff(): ManifestDiffLine[] {
  return [
    { op: " ", line: "apiVersion: apps/v1" },
    { op: " ", line: "kind: Deployment" },
    { op: " ", line: "metadata:" },
    { op: " ", line: "  name: order-service" },
    { op: " ", line: "spec:" },
    { op: "-", line: "  replicas: 3" },
    { op: "+", line: "  replicas: 5" },
    { op: " ", line: "  template:" },
    { op: " ", line: "    spec:" },
    { op: " ", line: "      containers:" },
    { op: " ", line: "      - name: order-service" },
    { op: "-", line: "        image: ghcr.io/coremonit/order-service:812" },
    { op: "+", line: "        image: ghcr.io/coremonit/order-service:813" },
    { op: " ", line: "        resources:" },
    { op: "-", line: "          limits: { cpu: 500m, memory: 512Mi }" },
    { op: "+", line: "          limits: { cpu: 1,    memory: 1Gi }" },
    { op: " ", line: "        env:" },
    { op: "+", line: "        - name: FEATURE_ASYNC_CHECKOUT" },
    { op: "+", line: "          value: \"true\"" },
  ];
}
